import {memo, useRef, useState} from "react";
import {Box, Grid} from "@mui/system";

import pxToRem from "hooks/usePxToRem";
import {CustomButton, CustomIconButton} from "ui";
import {FollowUpCard, NewFollowUpModal} from "./components";
import {DefaultCardStructure, EmailPreviewModal} from "components";
import ProcessFollowUpData from "./ProcessFollowUpData";
import {PostFollowUpType} from "./ProcessFollowUpTypes";
import {UseFormSetValue, useFormContext} from "react-hook-form";
import useSendFollowUp from "hooks/useSendFollowUp";
import {EmailData} from "components/EmailPreviewModal/EmailPreviewModal";
import {Process} from "views/ProcessDetails/refactor/types/process.types";
import {FollowUp} from "views/ProcessDetails/refactor/types/groupActivities.types";
import getModalityAndOperation from "util/getModalityAndOperation";
import {useResolvedProcessId} from "views/ProcessDetails/refactor/contexts/ResolvedProcessIdContext";

interface Props {
  refetchFollowUps: (setRhfValues: UseFormSetValue<Process>) => Promise<void>;
}

const ProcessFollowUps = (props: Props) => {
  const {refetchFollowUps} = props;
  const {watch, setValue, getValues} = useFormContext<Process>();

  const {postFollowUp, updateFollowUp} = ProcessFollowUpData();

  const cancelledProcess = getValues("agencyingSituation") === "Cancelado";

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [modalEdit, setModalEdit] = useState(false);

  const [currentFollowUp, setCurrentFollowUp] = useState<FollowUp>(
    {} as FollowUp,
  );

  const [followUpFilter, setFollowUpFilter] = useState<
    "private" | "public" | "all"
  >("all");

  const handleToggleNewFollowUpModal = () => {
    setModalIsOpen(!modalIsOpen);
  };

  const handlePostNewFollowUp = async (followUpData: PostFollowUpType) => {
    await postFollowUp(followUpData);
    await refetchFollowUps(setValue);
  };

  const handleUpdateFollowUp = async (
    id: number,
    followUpData: Partial<PostFollowUpType>,
    isEditing: boolean = true,
  ) => {
    await updateFollowUp(id, followUpData, isEditing);
    await refetchFollowUps(setValue);
  };

  const processId = useResolvedProcessId();

  const handleCreateAndSendFollow = async () => {
    await handleGetFollowUp(processId);
  };

  const formRef = useRef<HTMLFormElement>(null);

  const {
    isOpen,
    emailReturn,
    handleGetFollowUp,
    handleSendFollowUp,
    emailData,
    setEmailData,
    handleCloseModal,
  } = useSendFollowUp();

  const handleSendNewFollow = async (
    formRef: React.RefObject<HTMLFormElement>,
    localEmailData: EmailData,
    editedEmail: string,
  ) => {
    if (formRef.current?.reportValidity()) {
      await handleSendFollowUp(localEmailData, editedEmail);
    }
  };

  const handleFilterFollowUps = (
    followUps: FollowUp[] = [],
    filter: "private" | "public" | "all" = "all",
  ) => {
    switch (filter) {
      case "private":
        return followUps.filter((followUp) => followUp.isPrivate === true);
      case "public":
        return followUps.filter((followUp) => !followUp.isPrivate);
      case "all":
        return followUps;
    }
  };

  return (
    <DefaultCardStructure
      title="Acompanhamentos"
      linkId="followUps"
      hasDivider
      button={
        <CustomButton
          onClickFn={handleToggleNewFollowUpModal}
          disabled={cancelledProcess}
        >
          Novo acompanhamento
        </CustomButton>
      }
    >
      {isOpen && (
        <EmailPreviewModal
          processId={processId}
          isOpen={isOpen}
          handleClose={handleCloseModal}
          handleSendEmail={handleSendNewFollow}
          emailData={emailData}
          emailReturn={emailReturn}
          setEmailData={setEmailData}
          formRef={formRef}
        />
      )}

      {modalIsOpen && (
        <NewFollowUpModal
          isOpen={modalIsOpen}
          onCloseModal={handleToggleNewFollowUpModal}
          processNumber={watch("processNumber") ?? "-"}
          handlePostNewFollowUp={handlePostNewFollowUp}
          handleUpdateFollowUp={handleUpdateFollowUp}
          idGroupActivity={watch("idGroupActivity")}
          modalEdit={modalEdit}
          setModalEdit={setModalEdit}
          currentFollowUp={currentFollowUp}
          onCreateAndSendFollow={handleCreateAndSendFollow}
          modalType={
            getModalityAndOperation(
              getValues("modality"),
              getValues("operation"),
            ).short
          }
        />
      )}

      <Grid
        container
        sx={{
          display: "flex",
          flexDirection: "column",
          flexWrap: "nowrap",
          gap: pxToRem(24),
          maxHeight: pxToRem(500),
          padding: `${pxToRem(2)} ${pxToRem(12)} ${pxToRem(2)} ${pxToRem(2)}`,
          overflow: "auto",
        }}
      >
        <Box display="flex" gap={pxToRem(12)}>
          <CustomIconButton
            iconName="lock"
            size={"small"}
            tooltipText={
              followUpFilter !== "private" ? "Ver apenas privados" : undefined
            }
            onClick={() => setFollowUpFilter("private")}
            disabled={followUpFilter === "private"}
          />
          <CustomIconButton
            iconName="lock_open"
            size={"small"}
            tooltipText={
              followUpFilter !== "public" ? "Ver apenas públicos" : undefined
            }
            onClick={() => setFollowUpFilter("public")}
            disabled={followUpFilter === "public"}
          />
          <CustomIconButton
            iconName="visibility"
            size={"small"}
            tooltipText={followUpFilter !== "all" ? "Ver todos" : undefined}
            onClick={() => setFollowUpFilter("all")}
            disabled={followUpFilter === "all"}
          />
        </Box>
        {handleFilterFollowUps(
          watch("groupActivity.followUps"),
          followUpFilter,
        )?.map((followUp) => (
          <FollowUpCard
            key={followUp.id}
            followUp={followUp}
            setCurrentFollowUp={setCurrentFollowUp}
            handleOpenModal={() => {
              setModalIsOpen(true);
              setModalEdit(true);
            }}
            handleDeleteFollow={(id, followUpData) =>
              handleUpdateFollowUp(id, followUpData, false)
            }
          />
        ))}
      </Grid>
    </DefaultCardStructure>
  );
};

export default memo(ProcessFollowUps);
